import * as React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { AnimatePresence } from "motion/react";
import {
  StyledOverlay,
  StyledMotionOverlay,
  DraggableModalContent,
  StaticModalContent,
  ModalHeader,
  ModalDescription,
  ModalRail,
  ModalAction,
} from "./components";
import type { TypeModalProps } from "./ModalTypes";
import { getOverlayVariants } from "./MotionConfig";

// Type aliases for Dialog.Content event handlers
type InteractOutsideHandler = NonNullable<
  React.ComponentPropsWithoutRef<typeof Dialog.Content>["onInteractOutside"]
>;
type EscapeKeyDownHandler = NonNullable<
  React.ComponentPropsWithoutRef<typeof Dialog.Content>["onEscapeKeyDown"]
>;

/**
 * Accessible modal dialog component built on Radix UI Dialog primitives.
 *
 * This component provides a flexible modal implementation with comprehensive accessibility
 * features, keyboard navigation, and focus management built in.
 *
 * Key capabilities:
 * - Automatic ARIA attributes and focus trapping
 * - ESC key and outside click to close
 * - Simplified API with title/subtitle props for common use cases
 * - Controlled and uncontrolled state modes
 * - Optional draggable behavior for side-by-side interaction
 * - Floating action rail for quick actions like close, expand, etc.
 *
 * @example
 * // Simple uncontrolled modal
 * <Modal
 *   title="Delete Item"
 *   subtitle="This action cannot be undone"
 *   modalTrigger={<Button>Delete</Button>}
 * >
 *   <ModalBody>Are you sure you want to delete this item?</ModalBody>
 *   <ModalFooter
 *     cancelButton={<Button>Cancel</Button>}
 *     primaryButton={<Button appearance="destructive">Delete</Button>}
 *   />
 * </Modal>
 */
const Modal = (props: TypeModalProps) => {
  const {
    children,
    modalTrigger,
    draggable = false,
    open,
    defaultOpen,
    onOpenChange,
    "aria-label": label,
    title,
    subtitle,
    description,
    data = {},
    showOverlay = true,
    actions,
    closeButtonAriaLabel = "Close",
    closeButtonProps,
    zIndex = 6,
    // Extract Dialog.Content event handlers
    onOpenAutoFocus,
    onCloseAutoFocus,
    onEscapeKeyDown,
    onPointerDownOutside,
    onFocusOutside,
    onInteractOutside,
    // Extract convenience boolean props
    disableOutsideClickClose = false,
    disableEscapeKeyClose = false,
    disableDismiss = false,
    ...rest
  } = props;

  // Track open state for AnimatePresence
  // This state is synced via onOpenChange regardless of controlled/uncontrolled mode
  const [isOpen, setIsOpen] = React.useState(defaultOpen ?? false);

  const handleOpenChange = React.useCallback(
    (newOpen: boolean) => {
      // Always sync state for AnimatePresence
      setIsOpen(newOpen);
      // Call user's callback
      onOpenChange?.(newOpen);
    },
    [onOpenChange]
  );

  // Create data attributes object
  const dataAttributes = React.useMemo(() => {
    const attrs: Record<string, string> = {};
    Object.entries(data).forEach(([key, value]) => {
      attrs[`data-${key}`] = String(value);
    });
    attrs["data-qa-modal"] = "";
    // Only add open attribute if in controlled mode
    if (open !== undefined) {
      attrs["data-qa-modal-open"] = String(open);
    }
    return attrs;
  }, [data, open]);

  // Determine if we should auto-render the header from provided props
  const shouldRenderHeader = Boolean(title || subtitle);

  // Overlay animation variants
  const overlayVariants = getOverlayVariants();

  // Choose the appropriate content component based on draggable prop
  const ModalContentComponent = draggable
    ? DraggableModalContent
    : StaticModalContent;

  // Wrap event handlers to support convenience boolean props
  const wrappedOnInteractOutside = React.useCallback<InteractOutsideHandler>(
    (e) => {
      if (disableDismiss || disableOutsideClickClose) {
        e.preventDefault();
      }
      onInteractOutside?.(e);
    },
    [disableDismiss, disableOutsideClickClose, onInteractOutside]
  );

  const wrappedOnEscapeKeyDown = React.useCallback<EscapeKeyDownHandler>(
    (e) => {
      // If a V1 Popout is open, prevent the modal from closing.
      // The Popout's own escape handler (on document.body capture phase)
      // will fire next and close just the popout.
      const hasOpenPopout = document.querySelector("[data-qa-popout]") !== null;
      if (hasOpenPopout) {
        e.preventDefault();
      }

      // If a Base UI popup (Combobox, Select, Menu, etc.) is open inside this
      // dialog, prevent the modal from closing. The popup's own escape handler
      // will fire next and close just the popup, returning focus to its trigger.
      // Base UI positioners/popups carry `data-open` while mounted; triggers
      // use the distinct `data-popup-open` and won't match.
      const hasOpenBaseUIPopup =
        document.querySelector("[role='dialog'] [data-open]") !== null;
      if (hasOpenBaseUIPopup) {
        e.preventDefault();
      }

      if (disableDismiss || disableEscapeKeyClose) {
        e.preventDefault();
      }
      onEscapeKeyDown?.(e);
    },
    [disableDismiss, disableEscapeKeyClose, onEscapeKeyDown]
  );

  return (
    <Dialog.Root
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={handleOpenChange}
      modal={!draggable}
    >
      {/* Render trigger button if provided */}
      {modalTrigger && <Dialog.Trigger asChild>{modalTrigger}</Dialog.Trigger>}

      <Dialog.Portal forceMount>
        <AnimatePresence mode="wait">
          {(open ?? isOpen) && (
            <>
              {showOverlay && (
                <Dialog.Overlay asChild>
                  <StyledMotionOverlay
                    $zIndex={zIndex}
                    variants={overlayVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                  >
                    <StyledOverlay
                      data-slot="modal-overlay"
                      data-qa-modal-overlay
                      data-testid="modal-overlay"
                      allowInteraction={draggable}
                    />
                  </StyledMotionOverlay>
                </Dialog.Overlay>
              )}
              <ModalContentComponent
                label={label}
                dataAttributes={dataAttributes}
                draggable={draggable}
                zIndex={zIndex}
                rest={rest}
                dialogContentProps={{
                  onOpenAutoFocus,
                  onCloseAutoFocus,
                  onEscapeKeyDown: wrappedOnEscapeKeyDown,
                  onPointerDownOutside,
                  onFocusOutside,
                  onInteractOutside: wrappedOnInteractOutside,
                }}
              >
                {/* Floating actions rail - shows a close by default unless
                    disableDismiss is set (e.g. mandatory modals) */}
                <ModalRail>
                  {!disableDismiss && (
                    <ModalAction
                      actionType="close"
                      iconName="x-outline"
                      {...closeButtonProps}
                      aria-label={
                        (closeButtonProps as any)?.["aria-label"] ??
                        closeButtonAriaLabel
                      }
                    />
                  )}
                  {actions?.map((action, idx) => (
                    <ModalAction key={idx} {...action} />
                  ))}
                </ModalRail>
                {/* Auto-render header when title or subtitle is provided */}
                {shouldRenderHeader && (
                  <ModalHeader title={title} subtitle={subtitle} />
                )}

                {/* Auto-render description when provided */}
                {description && (
                  <ModalDescription>{description}</ModalDescription>
                )}

                {/* Main content */}
                {children}
              </ModalContentComponent>
            </>
          )}
        </AnimatePresence>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

/**
 * Hook for adding proper ARIA attributes to external modal triggers.
 *
 * ⚠️ **NOT RECOMMENDED** - Prefer using modalTrigger prop or ModalTrigger component.
 * Use this hook ONLY as a last resort when architectural constraints prevent keeping
 * the trigger inside the Modal component tree.
 *
 * **Important Limitations:**
 * - This hook only provides ARIA attributes (aria-haspopup, aria-expanded)
 * - Focus restoration is NOT automatic - you must manually handle it with refs
 * - Radix UI cannot track external triggers for proper accessibility
 *
 * **Why modalTrigger prop is better:**
 * - Automatic ARIA attributes
 * - Automatic focus restoration
 * - Better touch device support
 * - Follows WAI-ARIA Dialog best practices
 *
 * @param isOpen - Current open state of the modal
 * @param modalId - Optional ID of the modal element for aria-controls
 * @returns Object with ARIA attributes to spread onto trigger element
 *
 * @example
 * ```tsx
 * // Manual focus restoration required
 * const [isOpen, setIsOpen] = useState(false);
 * const triggerRef = useRef<HTMLButtonElement>(null);
 * const triggerProps = useModalTriggerProps(isOpen);
 *
 * return (
 *   <>
 *     <Button
 *       ref={triggerRef}
 *       {...triggerProps}
 *       onClick={() => setIsOpen(true)}
 *     >
 *       Open Modal
 *     </Button>
 *     <Modal
 *       open={isOpen}
 *       onOpenChange={setIsOpen}
 *       onCloseAutoFocus={(e) => {
 *         e.preventDefault();
 *         triggerRef.current?.focus();
 *       }}
 *     >
 *       <ModalBody>Content</ModalBody>
 *     </Modal>
 *   </>
 * );
 * ```
 */
export function useModalTriggerProps(
  isOpen: boolean,
  modalId?: string
): {
  "aria-haspopup": "dialog";
  "aria-expanded": boolean;
  "aria-controls"?: string;
} {
  return React.useMemo(
    () => ({
      "aria-haspopup": "dialog" as const,
      "aria-expanded": isOpen,
      ...(modalId ? { "aria-controls": modalId } : {}),
    }),
    [isOpen, modalId]
  );
}

/**
 * Hook for managing external modal triggers with automatic focus restoration.
 *
 * ⚠️ **NOT RECOMMENDED** - Prefer using modalTrigger prop or ModalTrigger component.
 * Use this hook ONLY as a last resort when architectural constraints prevent keeping
 * the trigger inside the Modal component tree.
 *
 * This hook improves upon useModalTriggerProps by managing the trigger ref internally
 * and providing the onCloseAutoFocus callback, eliminating the need for manual
 * focus restoration boilerplate.
 *
 * **Improvements over useModalTriggerProps:**
 * - ✅ No manual ref creation
 * - ✅ Automatic focus restoration via onCloseAutoFocus callback
 * - ✅ Automatic ARIA attributes
 *
 * **Why modalTrigger prop is still better:**
 * - Better touch device support
 * - Follows WAI-ARIA Dialog best practices
 * - Less boilerplate overall
 *
 * @param modalId - Optional ID of the modal element for aria-controls
 * @returns Object with triggerRef, ARIA props, and onCloseAutoFocus callback
 *
 * @example
 * ```tsx
 * const [isOpen, setIsOpen] = useState(false);
 * const { triggerRef, triggerProps, onCloseAutoFocus } = useModalExternalTrigger();
 *
 * return (
 *   <>
 *     <Button
 *       ref={triggerRef}
 *       {...triggerProps(isOpen)}
 *       onClick={() => setIsOpen(true)}
 *     >
 *       Open Modal
 *     </Button>
 *     <Modal
 *       open={isOpen}
 *       onOpenChange={setIsOpen}
 *       onCloseAutoFocus={onCloseAutoFocus}
 *     >
 *       <ModalBody>Content</ModalBody>
 *     </Modal>
 *   </>
 * );
 * ```
 */
export function useModalExternalTrigger<
  T extends HTMLElement = HTMLButtonElement
>(modalId?: string) {
  const triggerRef = React.useRef<T>(null);

  const triggerProps = React.useCallback(
    (isOpen: boolean) => ({
      "aria-haspopup": "dialog" as const,
      "aria-expanded": isOpen,
      ...(modalId ? { "aria-controls": modalId } : {}),
    }),
    [modalId]
  );

  const onCloseAutoFocus = React.useCallback((e: Event) => {
    e.preventDefault();
    triggerRef.current?.focus();
  }, []);

  return { triggerRef, triggerProps, onCloseAutoFocus };
}

export default Modal;
